import type {
  FoodActivity,
  DeepPartial,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
} from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"

import { usePolyglot } from "src/contexts"
import { useSliderInputProps } from "src/hooks"

interface Props {
  activity: DeepPartial<FoodActivity>;
  updateActivity: (a: DeepPartial<FoodActivity>) => void;
}

const id = "food-calories-range-input"
const min = 0
const max = 2000
const sliderStep = 25
const inputStep = 1
const polyglotPrefix = "components.entry_modal.food.calories."

export function CaloriesRangeInput(props: Props) {
  const {
    activity,
    updateActivity,
  } = props

  const polyglot = usePolyglot()

  const onChange = useCallback(
    (calories: number | null) => {
      updateActivity({
        ...activity,
        calories,
      })
    },
    [
      activity,
      updateActivity,
    ],
  )

  const value = typeof activity.calories === "number"
    ? activity.calories
    : null

  const {
    inputValue,
    sliderValue,
    onNumberChange,
    onSliderChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    min,
    max,
    inputStep,
    sliderStep,
    value,
    onChange,
    allowEmptyValue: true,
    emptyValue: null,
  })

  return (
    <>
      <SliderValue sizePreset="large">
        <SliderValueInput
          min={min}
          max={max}
          step={inputStep}
          value={inputValue}
          onChange={onNumberChange}
          onBlur={onNumberBlur}
          onKeyDown={onNumberKeyDown}
        />
        <SliderValueLabelContainer>
          <SliderValueLabel htmlFor={id}>
            {polyglot.t(`${polyglotPrefix}label_detail`)}
          </SliderValueLabel>
        </SliderValueLabelContainer>
      </SliderValue>
      <Slider>
        <SliderInput
          min={min}
          max={max}
          step={sliderStep}
          id={id}
          value={sliderValue ?? min}
          onChange={onSliderChange}
        />
        <SliderTracks
          min={min}
          max={max}
        />
      </Slider>
    </>
  )
}
